/**
 * Phase 9 (Phase 20: per campaign group) — switching a group's Auto-send
 * ON: real SMS, real money, so the confirm says what happens now — the
 * group's drafts still due today and their SMS, the stop-loss that halts it,
 * its daily cap, and that Kill stops every group's next send.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC, ReactNode } from 'react';
import { CalendarClock, Gauge, Send, ShieldAlert, Skull } from 'lucide-react';
import { type DropGenSendToday, stopLossOn } from '@trafficpulse/shared';

import { ConfirmDialog } from '@/components/ui';

import type { DropGenPlanDetail } from '../../../drop-generator.types';
import { type AutoSendMode, dkThenLocal, MODE_LINE } from './autosend-fact';
import { type AutoSendLayer, describeStopLoss } from './settings/autosend-draft';

const n = (v: number) => v.toLocaleString('en-US');

const Line: FC<{ icon: typeof Send; tint: string; children: ReactNode }> = ({ icon: Icon, tint, children }) => (
  <li className="flex items-start gap-2.5">
    <Icon className={`mt-0.5 h-4 w-4 shrink-0 ${tint}`} />
    <span>{children}</span>
  </li>
);

export const AutoSendConfirmDialog: FC<{
  open: boolean;
  plan: DropGenPlanDetail;
  /** The campaign group being switched on, with its settings as they will be. */
  layer: AutoSendLayer;
  today: DropGenSendToday | null;
  /** What "on" does for this group: real SMS, or simulated ones (20/10/2026). */
  mode: AutoSendMode;
  busy: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}> = ({ open, plan, layer, today, mode, busy, onConfirm, onCancel }) => {
  const due =
    today && today.pendingDrops > 0
      ? `${n(today.pendingDrops)} drafts are still due today — ${n(today.pendingSms)} SMS${today.nextAtUtc ? `, the next at ${dkThenLocal(today.nextAtUtc, plan.recipientTimezone)}` : ''}.`
      : `No draft of ${layer.name} is due today yet — the next laid-out day sends by itself.`;
  const ahead = layer.config.dispatchMode === 'SCHEDULE_AHEAD';
  const cap = layer.config.dailySmsCap;
  return (
    <ConfirmDialog
      open={open}
      title={ahead ? `Schedule ahead for ${layer.name}?` : `Turn Auto-send on for ${layer.name}?`}
      confirmLabel={mode === 'real' ? 'Turn on — real SMS' : mode === 'simulated' ? 'Turn on — simulated' : 'Turn on'}
      busy={busy}
      onConfirm={onConfirm}
      onCancel={onCancel}
      description={
        <div className="flex flex-col gap-3 text-sm">
          <p className={mode === 'real' ? '' : 'font-medium text-warning'}>{MODE_LINE[mode]}</p>
          <ul className="flex flex-col gap-2">
            <Line icon={Send} tint="text-success">
              {due}
            </Line>
            {plan.autoGenerate ? null : (
              <Line icon={CalendarClock} tint="text-primary">
                Auto-generate turns on too — auto-send only sends a day once it is laid out, and Auto-generate lays it out by itself at
                the drop generation time every day.
              </Line>
            )}
            {ahead ? (
              <Line icon={Send} tint="text-primary">
                Ready drops are submitted to SMSWiser now with their scheduled times, up to 24 hours ahead. They can still send while
                TrafficPulse is offline. Pending drops can be updated or cancelled before they start.
              </Line>
            ) : null}
            <Line icon={ShieldAlert} tint="text-warning">
              Stop-loss — {describeStopLoss(layer.config)}{stopLossOn(layer.config) ? `; it halts ${layer.name} only` : ''}.
            </Line>
            <Line icon={Gauge} tint="text-primary">
              {cap
                ? `Daily cap — ${n(cap)} SMS across ${layer.name}; your sends and the engine’s both count.`
                : `No daily cap for ${layer.name} — only ${stopLossOn(layer.config) ? 'the stop-loss and ' : ''}Kill stop a day.`}
            </Line>
            <Line icon={Skull} tint="text-destructive">
              Kill holds new submissions and requests cancellation of pending remote schedules. A drop already started cannot be recalled. A
              local draft more than 30 min late waits for you.
            </Line>
          </ul>
        </div>
      }
    />
  );
};
